import { useState } from 'react';
import { FaTrash, FaEnvelope, FaCheck } from 'react-icons/fa';
import { doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';

export default function ContactTable({ contacts, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  async function handleDelete(id) {
    if (window.confirm('Are you sure you want to delete this contact submission?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'contactSubmissions', id));
        toast.success('Contact deleted successfully');
        onUpdate();
      } catch (error) {
        console.error('Error deleting contact:', error);
        toast.error('Failed to delete contact');
      } finally {
        setLoading(false);
      }
    }
  }

  async function markAsRead(id) {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'contactSubmissions', id), {
        status: 'read',
      });
      toast.success('Marked as read');
      onUpdate();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  }

  if (!contacts || contacts.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <p className="text-gray-500">No contact submissions found</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((contact) => (
                <tr 
                  key={contact.id} 
                  className={`hover:bg-gray-50 cursor-pointer ${
                    contact.status === 'unread' ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => setSelectedContact(contact)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {contact.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{contact.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{contact.phone}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {contact.subject}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.timestamp?.toDate().toLocaleDateString() || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      contact.status === 'read'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {contact.status || 'unread'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAsRead(contact.id);
                        }}
                        disabled={loading || contact.status === 'read'}
                        className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        title="Mark as Read"
                      >
                        <FaCheck />
                      </button>
                      <a
                        href={`mailto:${contact.email}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:text-blue-900"
                        title="Send Email"
                      >
                        <FaEnvelope />
                      </a>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(contact.id);
                        }}
                        disabled={loading}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal for viewing full message */}
      {selectedContact && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedContact(null)}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold mb-4">Contact Details</h3>
            <div className="space-y-3">
              <div>
                <label className="font-semibold text-gray-700">Name:</label>
                <p className="text-gray-900">{selectedContact.name}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-700">Email:</label>
                <p className="text-gray-900">{selectedContact.email}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-700">Phone:</label>
                <p className="text-gray-900">{selectedContact.phone}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-700">Subject:</label>
                <p className="text-gray-900">{selectedContact.subject}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-700">Message:</label>
                <p className="text-gray-900 whitespace-pre-wrap">{selectedContact.message}</p>
              </div>
              <div>
                <label className="font-semibold text-gray-700">Date:</label>
                <p className="text-gray-900">
                  {selectedContact.timestamp?.toDate().toLocaleString() || 'N/A'}
                </p>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <a
                href={`mailto:${selectedContact.email}`}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Reply via Email
              </a>
              <button
                onClick={() => setSelectedContact(null)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
